import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPatch, operationsClient } from '@/lib/api/client';
import { taxCodeEndpoints } from '@/lib/api/endpoints';
import { taxZoneKeys, taxGroupKeys } from '@/lib/queryKeys';

/**
 * Multi-dim override value (Brian's backlog #41). Accepts:
 *  - legacy single string ('materials')
 *  - multi-dim array (['materials','labor'])
 *  - null → clear the override (restore Ziptax-derived value)
 */
export type AppliesToOverrideValue =
  | 'all'
  | 'materials'
  | 'labor'
  | 'freight'
  | Array<'all' | 'materials' | 'labor' | 'freight'>
  | null;

/**
 * Set or clear the per-tenant applies-to override on a tax code.
 *
 * `override = null` or `[]` is the restore exit door: the effective
 * `appliesTo` reverts to the Ziptax-derived value.
 * `override = ['materials', 'labor']` sets the multi-dim override —
 * the tax code applies to both categories simultaneously.
 */
export function useSetAppliesToOverride() {
  const qc = useQueryClient();
  return useApiMutation<void, { id: string; override: AppliesToOverrideValue }>({
    mutationFn: ({ id, override }) =>
      apiPatch<void>(operationsClient, taxCodeEndpoints.setAppliesToOverride(id), { override }),
    onSuccess: (_data, vars) => {
      const v = vars.override;
      const isClear = v === null || (Array.isArray(v) && v.length === 0);
      const label = Array.isArray(v) ? v.join(', ') : v;
      toast.success(
        isClear ? 'Restored to Ziptax-derived applies-to.' : `Override set to "${label}".`,
      );
      void qc.invalidateQueries({ queryKey: taxZoneKeys.all() });
      void qc.invalidateQueries({ queryKey: taxGroupKeys.all() });
    },
  });
}
